import { useId, useState } from 'react';
import { CalendarIcon } from 'lucide-react';

import { Calendar } from '@/components/Calendar/Calendar';
import { ClockRow } from '@/components/DatePicker/DateField';
import { formatDateTime, isValidTime, parseDateTime } from '@/components/DateTimeField/datetime';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover/Popover';
import { cn } from '@/util/utils';

export interface DateTimeFieldProps {
    id?: string;
    /** `dd-Mmm-yyyy · HH:MM`, or `''` when unset. */
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    invalid?: boolean;
    disabled?: boolean;
    'aria-describedby'?: string;
}

/**
 * ui-date[time], READ 2934:5932: one field for a date AND a time, because our own calendar has
 * no clock in it and the two used to sit in separate boxes (03 Sep review, built 04 Sep - the
 * gap the ticket calls out). The popover carries the 24-hour clock row under the calendar, with
 * Done in it; picking a day does not close the popover on its own, only Done (or a click
 * outside) does, so a day picked first waits for the time before the field commits anything.
 */
export function DateTimeField({
    id,
    value,
    onChange,
    placeholder = 'dd-mmm-yyyy · hh:mm',
    invalid,
    disabled,
    'aria-describedby': describedBy,
}: DateTimeFieldProps) {
    const generatedId = useId();
    const triggerId = id ?? generatedId;
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    id={triggerId}
                    disabled={disabled}
                    aria-invalid={invalid || undefined}
                    aria-describedby={describedBy}
                    data-slot="date-time-field-trigger"
                    className={cn(
                        'bg-card border-input text-foreground flex h-9 w-full min-w-0 items-center gap-2 rounded-md border px-3 text-left text-sm shadow-xs pointer-coarse:min-h-11 pointer-coarse:text-base',
                        'focus-visible:border-ring transition-[color,box-shadow,border-color] duration-(--duration-fast) outline-none focus-visible:shadow-[0_0_0_3px_var(--outline)]',
                        'aria-invalid:border-destructive disabled:bg-muted disabled:text-text-locked disabled:cursor-not-allowed disabled:shadow-none',
                    )}
                >
                    <CalendarIcon aria-hidden className="text-muted-foreground size-4 shrink-0" />
                    <span
                        className={cn(
                            'min-w-0 flex-1 truncate',
                            value ? undefined : 'text-muted-foreground',
                        )}
                    >
                        {value || placeholder}
                    </span>
                </button>
            </PopoverTrigger>
            {/* The picker's own popover: 16px in, the month then the clock row 12px apart. */}
            <PopoverContent align="start" className="flex w-auto flex-col gap-3">
                {/*
                  Only mounted while open, deliberately: a fresh Editor picks up `value` again on
                  every open (so a day picked and abandoned last time is gone), and its day/time
                  state needs no effect to stay in sync with a value that can also change from
                  outside (loading an existing lookbook).
                */}
                {open ? (
                    <Editor
                        value={value}
                        onCommit={(next) => {
                            onChange(next);
                            setOpen(false);
                        }}
                    />
                ) : null}
            </PopoverContent>
        </Popover>
    );
}

function Editor({ value, onCommit }: { value: string; onCommit: (value: string) => void }) {
    const initial = parseDateTime(value);
    const [day, setDay] = useState(initial.day);
    const [time, setTime] = useState(initial.time);
    const clockId = useId();

    return (
        <>
            <Calendar mode="single" selected={day} onSelect={setDay} autoFocus defaultMonth={day} />
            {/* The same clock row DateField draws: Time, one 88px hh:mm box and Done (2934:5932). */}
            <ClockRow
                id={clockId}
                clock={time}
                onClock={(next) => setTime(next ?? '')}
                doneDisabled={!day || !isValidTime(time)}
                onDone={() => {
                    const next = formatDateTime(day, time);
                    if (next) onCommit(next);
                }}
            />
        </>
    );
}
